import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import Select from 'components/ui/Select';
import getPassengerLabel from 'utils/getPassengerLabel';

import { bookingShape, historyShape } from 'constants/propTypes';
import { Row, Column } from 'components/shared/Layout';
import { ActionButton, ConfirmButton } from 'components/ui/Button';
import { sortLegsByDate } from 'utils/sortLegsByDate';
import { bookingStatus } from 'types/booking';
import config from 'utils/config';

import { VerticalLine, StyledExtrasContainer, StyledLegContainer, ManageExtrasButtonContainer } from '../Extras.styles';
import ExtrasJourneyInfo from './ExtrasJourneyInfo';
import ExtrasJourneyProduct from './ExtrasJourneyProduct';
import { hasExtras, filterMargins, bookingContainOnlyUnRebookableItems } from '../Extras.utils';
import { isBookingMultiJourney } from 'utils/booking';
import { NOT_THIRD_PARTY_BOOKING } from 'types/booking';

import Modal from 'components/ui/Modal';
import { StyledAbandonButton } from 'components/shared/OnHoldCancellationModal/OnHoldCancellationModal.styles';

const ExtrasInTrainSegment = ({ intl, history, isEurostarAgent, booking, onCheckboxChange, itemsToCancel, refetch }) => {
  const [showHeterogenousModal, setShowHeterogenousModal] = useState(false);
  const [passenger, setPassenger] = useState('');
  const isBookingPathExtrasEnabled = config('ENABLE_EXTRAS_BOOKING_PATH', { canOverride: true }) === 'true';
  const isDisableAddingExtrasOnThirdPartyEnabled = config('DISABLE_ADDING_EXTRAS_ON_THIRD_PARTY', { canOverride: true }) === 'true';

  const outboundSortedByDate = filterMargins([...booking.outbound].sort(sortLegsByDate));
  const inboundSortedByDate = filterMargins([...booking.inbound].sort(sortLegsByDate));

  const hasOutboundItems = outboundSortedByDate.length > 0;
  const hasInboundItems = inboundSortedByDate.length > 0;
  const isExtrasVisible = hasExtras(booking) && (hasOutboundItems || hasInboundItems);

  const activePassengers = useMemo(() => {
    const travelingPassengers = new Set();

    booking.allBookingItems.forEach(({ passengerId, cancelled, canBeRebooked }) => {
      if (!cancelled && canBeRebooked) {
        travelingPassengers.add(passengerId);
      }
    });

    return booking.passengers.filter(({ id }) => travelingPassengers.has(id));
  }, [booking.allBookingItems, booking.passengers]);

  const hasOnlyUnRebookableItems = bookingContainOnlyUnRebookableItems({ outbound: booking.outbound, inbound: booking.inbound });

  const onManageExtrasClick = () => {
    if (!booking.isHomogeneous) {
      setShowHeterogenousModal(true);
      return;
    }

    history.push(`/manage/bookings/${booking.reference}/extras/manage`);
  };

  const multiJourneyBooking = isBookingMultiJourney(booking?.additionalDetails);
  const disableAddingExtrasForThirdPartyBooking =
    isDisableAddingExtrasOnThirdPartyEnabled && !NOT_THIRD_PARTY_BOOKING.includes(booking.salesChannelCode);

  return (
    <Row span={14} start={2}>
      <Modal
        isOpen={showHeterogenousModal}
        title={<FormattedMessage id="manageBooking.addExtraHeterogenousTitle" />}
        subtitle={
          <>
            <FormattedMessage id="manageBooking.addExtraHeterogenousPassengers" />
          </>
        }
        onClose={() => setShowHeterogenousModal(false)}
      >
        <Select defaultValue={''} name={'passengerId'} data-testid={'passengerId'} onChange={(event) => setPassenger(event.target.value)}>
          <option disabled value="">
            Select passengers
          </option>

          {booking.passengers.map((passenger) => (
            <option key={passenger.id} value={passenger.id} disabled={!activePassengers.find((activePax) => activePax.id === passenger.id)}>
              {getPassengerLabel(intl, passenger, booking.isGroupBooking)}
            </option>
          ))}
        </Select>

        <ConfirmButton
          data-testid={'addExtras'}
          onClick={() => history.push(`/manage/bookings/${booking.reference}/extras/manage?passengerId=${passenger}`)}
          disabled={passenger === ''}
        >
          <FormattedMessage id="extras.addOrAmendExtras" />
        </ConfirmButton>

        <StyledAbandonButton data-testid={'cancelExtras'} onClick={() => setShowHeterogenousModal(false)}>
          <FormattedMessage id="extras.cancel" />
        </StyledAbandonButton>
      </Modal>

      <Row>
        <Column span={8}>
          <h1>
            <FormattedMessage id="extras.title" />
          </h1>
        </Column>
        {isBookingPathExtrasEnabled ? (
          <Column span={8}>
            <ManageExtrasButtonContainer>
              <ActionButton
                data-testid="manage-extras"
                disabled={
                  !isEurostarAgent ||
                  booking.displayStatus !== bookingStatus.CONFIRMED ||
                  multiJourneyBooking ||
                  hasOnlyUnRebookableItems ||
                  disableAddingExtrasForThirdPartyBooking
                }
                onClick={onManageExtrasClick}
              >
                <FormattedMessage id="extras.addOrAmendExtras" />
              </ActionButton>
            </ManageExtrasButtonContainer>
          </Column>
        ) : null}
      </Row>

      <Row>
        {isExtrasVisible ? (
          <StyledExtrasContainer>
            <Column span={8}>
              <VerticalLine>
                <h2>
                  <FormattedMessage id="extras.outbound" />
                </h2>

                {hasOutboundItems ? (
                  outboundSortedByDate.map((outbound) => (
                    <StyledLegContainer key={`${outbound.serviceType.code}${outbound.trainNumber}`}>
                      <ExtrasJourneyInfo direction={outbound} />
                      <ExtrasJourneyProduct
                        direction={outbound}
                        disabled={!isEurostarAgent || booking.status !== bookingStatus.CONFIRMED}
                        onCheckboxChange={onCheckboxChange}
                        itemsToCancel={itemsToCancel}
                        booking={booking}
                        refetch={refetch}
                      />
                    </StyledLegContainer>
                  ))
                ) : (
                  <FormattedMessage id="extras.noInboundMessage" />
                )}
              </VerticalLine>
            </Column>

            <Column span={8}>
              <h2>
                <FormattedMessage id="extras.inbound" />
              </h2>

              {hasInboundItems ? (
                inboundSortedByDate.map((inbound) => (
                  <StyledLegContainer key={`${inbound.serviceType.code}${inbound.trainNumber}`}>
                    <ExtrasJourneyInfo direction={inbound} />
                    <ExtrasJourneyProduct
                      disabled={!isEurostarAgent || booking.status !== bookingStatus.CONFIRMED}
                      direction={inbound}
                      onCheckboxChange={onCheckboxChange}
                      itemsToCancel={itemsToCancel}
                      booking={booking}
                      refetch={refetch}
                    />
                  </StyledLegContainer>
                ))
              ) : (
                <FormattedMessage id="extras.noInboundMessage" />
              )}
            </Column>
          </StyledExtrasContainer>
        ) : (
          <Column>
            <FormattedMessage id="extras.noProductsBookingLevel" />
          </Column>
        )}
      </Row>
    </Row>
  );
};

ExtrasInTrainSegment.propTypes = {
  intl: intlShape.isRequired,
  booking: bookingShape,
  history: historyShape,
  onCheckboxChange: PropTypes.func,
  itemsToCancel: PropTypes.arrayOf(PropTypes.string),
  isEurostarAgent: PropTypes.bool,
  refetch: PropTypes.func.isRequired
};

export default injectIntl(ExtrasInTrainSegment);
